"use client";

import { c as _c } from "react/compiler-runtime";
import { IconArrowRight, IconCircleX, IconSearch, IconX } from "@tabler/icons-react";
import { formatForDisplay, useHotkey } from "@tanstack/react-hotkeys";
import { useAtom } from "jotai";
import { useParams } from "next/navigation";
import posthogClient from "posthog-js";
import { useCallback, useEffect, useEffectEvent, useLayoutEffect, useRef, useState, useTransition } from "react";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { useRouter } from "@/hooks/use-router";
import { pendingDomainAtom } from "@/lib/atoms/search-atoms";
import { safeDecodeURIComponent } from "@/lib/safe-parse";
import { Field, FieldLabel } from "@domainstack/ui/field";
import { Form } from "@domainstack/ui/form";
import { useIsClient } from "@domainstack/ui/hooks";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@domainstack/ui/input-group";
import { Kbd } from "@domainstack/ui/kbd";
import { Spinner } from "@domainstack/ui/spinner";
import { cn } from "@domainstack/ui/utils";
import { isValidDomain, normalizeDomainInput } from "@domainstack/utils/domain/client";
const SEARCH_HOTKEY = "Mod+K";
type SearchClientVariant = "sm" | "lg";
export type SearchClientProps = {
  variant?: SearchClientVariant;
  initialValue?: string;
  /** Lets a parent focus the input imperatively; internal ref when omitted. */
  inputRef?: React.RefObject<HTMLInputElement | null>;
  /** Collapse without moving focus — used after submit and on an idle blur. */
  onCloseAction?: () => void;
  /** Collapse and return focus to the control that opened the search. */
  onDismissAction?: () => void;
  /** Runs before the hotkey focuses the input, so a collapsed parent can expand. */
  onHotkeyAction?: () => void;
};
function getRoutePrefill(routeDomain: string | undefined): string {
  if (!routeDomain) return "";
  return safeDecodeURIComponent(routeDomain) ?? "";
}
function getDerivedInitialValue(variant: SearchClientVariant, routeDomain: string | undefined, initialValue: string): string {
  const rawInitial = variant === "sm" ? getRoutePrefill(routeDomain) : initialValue;
  const normalizedInitial = normalizeDomainInput(rawInitial);
  return isValidDomain(normalizedInitial) ? normalizedInitial : "";
}
function getSearchPlaceholder(variant: SearchClientVariant, mounted: boolean, isMobile: boolean): string {
  if (variant === "lg") return "domainstack.io\u2026";
  if (mounted && isMobile) return "Search\u2026";
  return "Search any domain\u2026";
}
function SearchInputAddons(t0) {
  const $ = _c(10);
  const {
    variant,
    loading,
    mounted,
    isFocused,
    onDismissAction
  } = t0;
  if (variant === "sm" && (loading || mounted)) {
    let t1;
    if ($[0] !== isFocused || $[1] !== loading || $[2] !== onDismissAction) {
      t1 = <InputGroupAddon align="inline-end">{loading ? <Spinner /> : <>{onDismissAction ? <InputGroupButton size="icon-xs" className="md:hidden" aria-label="Close search" onPointerDown={_temp} onClick={() => onDismissAction()}><IconX /></InputGroupButton> : null}<Kbd className="hidden border bg-muted/80 px-1.5 py-0.5 md:inline-flex">{isFocused ? "Esc" : formatForDisplay(SEARCH_HOTKEY, {
              separatorToken: "\xA0"
            })}</Kbd></>}</InputGroupAddon>;
      $[0] = isFocused;
      $[1] = loading;
      $[2] = onDismissAction;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  if (variant === "lg") {
    let t1;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className="text-[13px]">Inspect</span>;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    let t2;
    if ($[5] !== loading) {
      t2 = loading ? <Spinner /> : <><Kbd className="hidden text-[13px] sm:inline-flex">⏎</Kbd><IconArrowRight className="inline-flex sm:hidden" aria-hidden={true} /></>;
      $[5] = loading;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t3;
    if ($[7] !== loading || $[8] !== t2) {
      t3 = <InputGroupAddon align="inline-end"><InputGroupButton type="submit" disabled={loading} className="mx-1 h-8 disabled:pointer-events-none" variant="ghost">{t1}{t2}</InputGroupButton></InputGroupAddon>;
      $[7] = loading;
      $[8] = t2;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    return t3;
  }
  return null;
}
function _temp(e) {
  return e.preventDefault();
}
function useSearchClient(t0) {
  const $ = _c(56);
  const {
    variant,
    initialValue,
    inputRef: externalInputRef,
    onCloseAction,
    onDismissAction,
    onHotkeyAction
  } = t0;
  const router = useRouter();
  const params = useParams();
  const isMobile = useIsMobile();
  const [pendingDomain, setPendingDomain] = useAtom(pendingDomainAtom);
  let t1;
  if ($[0] !== initialValue || $[1] !== params.domain || $[2] !== variant) {
    t1 = getDerivedInitialValue(variant, params.domain, initialValue);
    $[0] = initialValue;
    $[1] = params.domain;
    $[2] = variant;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const derivedInitial = t1;
  const [value, setValue] = useState(derivedInitial);
  const [prevDerivedInitial, setPrevDerivedInitial] = useState(derivedInitial);
  const [loading, startNavigation] = useTransition();
  const resetWhenHiddenRef = useRef(false);
  const mounted = useIsClient();
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  let t2;
  if ($[4] !== externalInputRef) {
    t2 = node => {
      inputRef.current = node;
      if (externalInputRef) {
        externalInputRef.current = node;
      }
    };
    $[4] = externalInputRef;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const attachInputRef = t2;
  const shouldAutoFocus = variant === "lg" && mounted && !isMobile;
  let t3;
  let t4;
  if ($[6] !== shouldAutoFocus) {
    t3 = () => {
      if (shouldAutoFocus && document.activeElement === document.body) {
        inputRef.current?.focus();
      }
    };
    t4 = [shouldAutoFocus];
    $[6] = shouldAutoFocus;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[9] !== variant) {
    t5 = () => () => {
      if (variant !== "lg" || !resetWhenHiddenRef.current) {
        return;
      }
      resetWhenHiddenRef.current = false;
      setValue("");
    };
    t6 = [variant];
    $[9] = variant;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useLayoutEffect(t5, t6);
  if (derivedInitial !== prevDerivedInitial) {
    setPrevDerivedInitial(derivedInitial);
    setValue(derivedInitial);
  }
  let t7;
  if ($[12] !== onHotkeyAction) {
    t7 = () => {
      onHotkeyAction?.();
      inputRef.current?.focus();
    };
    $[12] = onHotkeyAction;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      conflictBehavior: "allow",
      meta: {
        name: "Search domains",
        description: "Focus the site search",
        group: "Global"
      }
    };
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  useHotkey(SEARCH_HOTKEY, t7, t8);
  let t9;
  if ($[15] !== router || $[16] !== variant) {
    t9 = domain => {
      const target = normalizeDomainInput(domain);
      posthogClient.capture("search_submitted", {
        domain: target
      });
      if (variant === "lg") {
        resetWhenHiddenRef.current = true;
      }
      startNavigation(() => router.push(`/${encodeURIComponent(target)}`));
    };
    $[15] = router;
    $[16] = variant;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const navigateToDomain = t9;
  let t10;
  if ($[18] !== navigateToDomain || $[19] !== setPendingDomain) {
    t10 = domain_0 => {
      setPendingDomain(null);
      setValue(domain_0);
      navigateToDomain(domain_0);
    };
    $[18] = navigateToDomain;
    $[19] = setPendingDomain;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const searchSuggestion = useEffectEvent(t10);
  let t11;
  if ($[21] !== pendingDomain || $[22] !== searchSuggestion || $[23] !== variant) {
    t11 = () => {
      if (variant === "lg" && pendingDomain) {
        searchSuggestion(pendingDomain);
      }
    };
    $[21] = pendingDomain;
    $[22] = searchSuggestion;
    $[23] = variant;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== pendingDomain || $[26] !== variant) {
    t12 = [variant, pendingDomain];
    $[25] = pendingDomain;
    $[26] = variant;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  useEffect(t11, t12);
  const pointerDownRef = useRef(false);
  const justFocusedRef = useRef(false);
  let t13;
  if ($[28] !== variant) {
    t13 = () => {
      if (variant !== "sm") {
        return;
      }
      pointerDownRef.current = true;
    };
    $[28] = variant;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const handlePointerDown = t13;
  let t14;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = e => {
      setIsFocused(true);
      if (!pointerDownRef.current) {
        e.currentTarget.select();
        justFocusedRef.current = false;
      } else {
        justFocusedRef.current = true;
        pointerDownRef.current = false;
      }
    };
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const handleFocus = t14;
  let t15;
  if ($[31] !== derivedInitial || $[32] !== onCloseAction || $[33] !== value) {
    t15 = e_0 => {
      const next = e_0.relatedTarget;
      if (next instanceof Node && e_0.currentTarget.contains(next)) {
        return;
      }
      setIsFocused(false);
      if (value.trim() === "" || value === derivedInitial) {
        onCloseAction?.();
      }
    };
    $[31] = derivedInitial;
    $[32] = onCloseAction;
    $[33] = value;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  const handleBlur = t15;
  let t16;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = e_1 => {
      if (e_1.detail === 3) {
        e_1.currentTarget.select();
        justFocusedRef.current = false;
        return;
      }
      if (justFocusedRef.current && e_1.detail === 1) {
        e_1.currentTarget.select();
      }
      justFocusedRef.current = false;
    };
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  const handleClick = t16;
  let t17;
  if ($[36] !== derivedInitial || $[37] !== onDismissAction || $[38] !== value) {
    t17 = e_2 => {
      if (e_2.key !== "Escape" || e_2.nativeEvent.isComposing) {
        return;
      }
      e_2.preventDefault();
      e_2.stopPropagation();
      if (value !== derivedInitial) {
        setValue(derivedInitial);
        return;
      }
      e_2.currentTarget.blur();
      onDismissAction?.();
    };
    $[36] = derivedInitial;
    $[37] = onDismissAction;
    $[38] = value;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  const handleKeyDown = t17;
  let t18;
  if ($[40] !== navigateToDomain || $[41] !== onCloseAction || $[42] !== value) {
    t18 = () => {
      const normalized = normalizeDomainInput(value);
      if (!isValidDomain(normalized)) {
        posthogClient.capture("search_invalid_input", {
          input: value
        });
        toast.error("Please enter a valid domain.", {
          id: `search-invalid-input-${value}`,
          icon: <IconCircleX className="size-4" />,
          position: "bottom-center"
        });
        inputRef.current?.focus();
        return;
      }
      setIsFocused(false);
      inputRef.current?.blur();
      navigateToDomain(normalized);
      onCloseAction?.();
    };
    $[40] = navigateToDomain;
    $[41] = onCloseAction;
    $[42] = value;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  const handleSubmit = t18;
  let t19;
  if ($[44] !== attachInputRef || $[45] !== handleBlur || $[46] !== handleKeyDown || $[47] !== handlePointerDown || $[48] !== handleSubmit || $[49] !== isFocused || $[50] !== isMobile || $[51] !== loading || $[52] !== mounted || $[53] !== value || $[54] !== variant) {
    t19 = {
      variant,
      value,
      setValue,
      loading,
      mounted,
      isMobile,
      isFocused,
      attachInputRef,
      handlePointerDown,
      handleFocus,
      handleBlur,
      handleClick,
      handleKeyDown,
      handleSubmit
    };
    $[44] = attachInputRef;
    $[45] = handleBlur;
    $[46] = handleKeyDown;
    $[47] = handlePointerDown;
    $[48] = handleSubmit;
    $[49] = isFocused;
    $[50] = isMobile;
    $[51] = loading;
    $[52] = mounted;
    $[53] = value;
    $[54] = variant;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  return t19;
}
export function SearchClient(t0) {
  const $ = _c(41);
  const {
    variant: t1,
    initialValue: t2,
    inputRef: externalInputRef,
    onCloseAction,
    onDismissAction,
    onHotkeyAction
  } = t0;
  const variant = t1 === undefined ? "lg" : t1;
  const initialValue = t2 === undefined ? "" : t2;
  let t3;
  if ($[0] !== externalInputRef || $[1] !== initialValue || $[2] !== onCloseAction || $[3] !== onDismissAction || $[4] !== onHotkeyAction || $[5] !== variant) {
    t3 = {
      variant,
      initialValue,
      inputRef: externalInputRef,
      onCloseAction,
      onDismissAction,
      onHotkeyAction
    };
    $[0] = externalInputRef;
    $[1] = initialValue;
    $[2] = onCloseAction;
    $[3] = onDismissAction;
    $[4] = onHotkeyAction;
    $[5] = variant;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    value,
    setValue,
    loading,
    mounted,
    isMobile,
    isFocused,
    attachInputRef,
    handlePointerDown,
    handleFocus,
    handleBlur,
    handleClick,
    handleKeyDown,
    handleSubmit
  } = useSearchClient(t3);
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <FieldLabel className="sr-only">Domain</FieldLabel>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = variant === "lg" ? "h-12" : "h-10";
  let t6;
  if ($[8] !== t5) {
    t6 = cn(t5);
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== isMobile || $[11] !== mounted || $[12] !== variant) {
    t7 = getSearchPlaceholder(variant, mounted, isMobile);
    $[10] = isMobile;
    $[11] = mounted;
    $[12] = variant;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== setValue) {
    t8 = e => setValue(e.target.value);
    $[14] = setValue;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== attachInputRef || $[17] !== handleClick || $[18] !== handleFocus || $[19] !== handleKeyDown || $[20] !== handlePointerDown || $[21] !== loading || $[22] !== t7 || $[23] !== t8 || $[24] !== value) {
    t9 = <InputGroupInput ref={attachInputRef} name="q" inputMode="url" autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck={false} disabled={loading} placeholder={t7} aria-label="Search any domain" value={value} onChange={t8} onPointerDown={handlePointerDown} onFocus={handleFocus} onClick={handleClick} onKeyDown={handleKeyDown} className="relative truncate" />;
    $[16] = attachInputRef;
    $[17] = handleClick;
    $[18] = handleFocus;
    $[19] = handleKeyDown;
    $[20] = handlePointerDown;
    $[21] = loading;
    $[22] = t7;
    $[23] = t8;
    $[24] = value;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <InputGroupAddon><IconSearch aria-hidden={true} /></InputGroupAddon>;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== isFocused || $[28] !== loading || $[29] !== mounted || $[30] !== onDismissAction || $[31] !== variant) {
    t11 = <SearchInputAddons variant={variant} loading={loading} mounted={mounted} isFocused={isFocused} onDismissAction={onDismissAction} />;
    $[27] = isFocused;
    $[28] = loading;
    $[29] = mounted;
    $[30] = onDismissAction;
    $[31] = variant;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== handleBlur || $[34] !== t11 || $[35] !== t6 || $[36] !== t9) {
    t12 = <Field>{t4}<div className="relative w-full flex-1"><InputGroup className={t6} onBlur={handleBlur}>{t9}{t10}{t11}</InputGroup></div></Field>;
    $[33] = handleBlur;
    $[34] = t11;
    $[35] = t6;
    $[36] = t9;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== handleSubmit || $[39] !== t12) {
    t13 = <div className="flex w-full flex-col gap-5"><Form aria-label="Domain search" action="/" method="GET" onFormSubmit={handleSubmit}>{t12}</Form></div>;
    $[38] = handleSubmit;
    $[39] = t12;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  return t13;
}